// ============================================================
// MOBILE MENU + SUBMENU (Dynamic Slide)
// ============================================================

import { Collapse } from 'bootstrap';

const MOBILE_BREAKPOINT = 992;

function getDirectSubmenu(item) {
    if (!item) return null;

    const directChild = Array.from(item.children).find((child) => {
        return child instanceof HTMLElement && child.classList.contains('dropdown-menu');
    });

    return directChild instanceof HTMLElement ? directChild : null;
}

function getToggleButton(item) {
    if (!item) return null;

    const directButton = Array.from(item.children).find((child) => {
        return child instanceof HTMLElement && child.classList.contains('dropdown-toggle-custom');
    });
    if (directButton instanceof HTMLElement) return directButton;

    const rowContainer = Array.from(item.children).find((child) => {
        return child instanceof HTMLElement && (child.classList.contains('d-flex') || child.classList.contains('submenu-row'));
    });

    if (!(rowContainer instanceof HTMLElement)) return null;

    const rowButton = Array.from(rowContainer.children).find((child) => {
        return child instanceof HTMLElement && child.classList.contains('dropdown-toggle-custom');
    });

    return rowButton instanceof HTMLElement ? rowButton : null;
}

function setToggleState(button, isExpanded) {
    if (!button) return;
    button.classList.toggle('active', isExpanded);
    button.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
}

function closeBranch(item, { updateAncestors = true } = {}) {
    const menu = getDirectSubmenu(item);
    if (!menu) return;

    const nestedItems = menu.querySelectorAll('li.has-submenu');
    nestedItems.forEach((nestedItem) => {
        const nestedMenu = getDirectSubmenu(nestedItem);
        const nestedButton = getToggleButton(nestedItem);

        if (nestedMenu) {
            nestedMenu.classList.remove('show');
            nestedMenu.classList.remove('align-right');
        }

        setToggleState(nestedButton, false);
        nestedItem.classList.remove('open-left', 'open-right');
    });

    menu.classList.remove('show');
    menu.classList.remove('align-right');

    const toggleButton = getToggleButton(item);
    setToggleState(toggleButton, false);
    item.classList.remove('open-left', 'open-right');

    if (!updateAncestors) return;
}

function openBranch(item) {
    const menu = getDirectSubmenu(item);
    if (!menu) return;

    menu.classList.add('show');

    const toggleButton = getToggleButton(item);
    setToggleState(toggleButton, true);
}

function closeSiblingBranches(item) {
    const parentList = item?.parentElement;
    if (!parentList) return;

    Array.from(parentList.children).forEach((sibling) => {
        if (!(sibling instanceof HTMLElement)) return;
        if (sibling === item) return;
        if (!sibling.matches('li.has-submenu')) return;

        closeBranch(sibling, { updateAncestors: false });
    });
}

function resetAllSubmenus() {
    document.querySelectorAll('.navbar-nav .dropdown-menu').forEach((menu) => {
        menu.classList.remove('show', 'align-right');
    });

    document.querySelectorAll('.navbar-nav li.has-submenu').forEach((item) => {
        item.classList.remove('open-left', 'open-right');
    });

    document.querySelectorAll('.dropdown-toggle-custom').forEach((button) => {
        setToggleState(button, false);
    });
}

function initDesktopEdgeAwareSubmenus() {
    const submenuItems = Array.from(document.querySelectorAll('.navbar-nav li.has-submenu'))
        .filter((item) => getDirectSubmenu(item));

    const viewportGap = 12;

    const clearDirection = (item) => {
        item.classList.remove('open-left', 'open-right');
        const directSubmenu = getDirectSubmenu(item);
        if (directSubmenu) {
            directSubmenu.classList.remove('align-right');
        }
    };

    const setDirection = (item) => {
        if (window.innerWidth < MOBILE_BREAKPOINT) return;

        const directSubmenu = getDirectSubmenu(item);
        if (!directSubmenu) return;

        clearDirection(item);

        const itemRect = item.getBoundingClientRect();
        const submenuWidth = directSubmenu.offsetWidth || 220;
        const isTopLevel = item.classList.contains('nav-item');

        if (isTopLevel) {
            const topLevelWouldOverflow = itemRect.left + submenuWidth > (window.innerWidth - viewportGap);
            if (topLevelWouldOverflow) {
                directSubmenu.classList.add('align-right');
            }
            return;
        }

        const overflowsRight = itemRect.right + submenuWidth > (window.innerWidth - viewportGap);
        const fitsOnLeft = itemRect.left - submenuWidth >= viewportGap;

        if (overflowsRight && fitsOnLeft) {
            item.classList.add('open-left');
            return;
        }

        item.classList.add('open-right');
    };

    submenuItems.forEach((item) => {
        item.addEventListener('mouseenter', () => {
            if (window.innerWidth < MOBILE_BREAKPOINT) return;
            window.requestAnimationFrame(() => setDirection(item));
        });

        item.addEventListener('focusin', () => {
            if (window.innerWidth < MOBILE_BREAKPOINT) return;
            window.requestAnimationFrame(() => setDirection(item));
        });

        item.addEventListener('mouseleave', () => {
            if (window.innerWidth < MOBILE_BREAKPOINT) return;

            window.setTimeout(() => {
                if (!item.matches(':hover') && !item.matches(':focus-within')) {
                    clearDirection(item);
                }
            }, 120);
        });

        item.addEventListener('focusout', () => {
            if (window.innerWidth < MOBILE_BREAKPOINT) return;

            window.setTimeout(() => {
                if (!item.matches(':hover') && !item.matches(':focus-within')) {
                    clearDirection(item);
                }
            }, 0);
        });
    });

    return {
        recalculateOpenDirections() {
            if (window.innerWidth < MOBILE_BREAKPOINT) return;

            submenuItems.forEach((item) => {
                if (item.matches(':hover') || item.matches(':focus-within')) {
                    setDirection(item);
                }
            });
        }
    };
}

export function initMobileMenu(body) {
    const navbarToggler = document.querySelector('.navbar-toggler');
    const navbarCollapse = document.querySelector('.navbar-collapse');
    const bsCollapse = navbarCollapse ? new Collapse(navbarCollapse, { toggle: false }) : null;

    const desktopSubmenuApi = initDesktopEdgeAwareSubmenus();

    // --- Blokada scrolla przy otwartym menu ---
    if (navbarCollapse) {
        navbarCollapse.addEventListener('show.bs.collapse', () => {
            body.style.overflow = 'hidden';
            body.classList.add('mobile-menu-open');
        });

        navbarCollapse.addEventListener('hide.bs.collapse', () => {
            body.style.overflow = '';
            body.classList.remove('mobile-menu-open');
        });

        navbarCollapse.addEventListener('hidden.bs.collapse', () => {
            resetAllSubmenus();
        });
    }

    // --- Zamknij menu po kliknięciu linku (mobile) ---
    const navLinks = document.querySelectorAll('.navbar-nav .nav-link, .navbar-nav .dropdown-item');
    navLinks.forEach((link) => {
        link.addEventListener('click', () => {
            const isMobileTogglerVisible = navbarToggler && window.getComputedStyle(navbarToggler).display !== 'none';
            if (!isMobileTogglerVisible) return;

            const ownListItem = link.closest('li');
            const hasDirectSubmenu = !!getDirectSubmenu(ownListItem);
            const isPlaceholderParentLink = hasDirectSubmenu && link.getAttribute('href') === '#';

            if (isPlaceholderParentLink) {
                return;
            }

            if (bsCollapse) bsCollapse.hide();
        });
    });

    // --- Submenu mobile: wielopoziomowy accordion z animacją wysokości ---
    document.querySelectorAll('.navbar-nav .dropdown-toggle-custom').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
    });

    const navbarNav = document.querySelector('.navbar-nav');
    navbarNav?.addEventListener('click', (e) => {
        const target = e.target instanceof Element ? e.target : null;
        const toggle = target?.closest('.dropdown-toggle-custom');

        if (!toggle) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        if (window.innerWidth >= MOBILE_BREAKPOINT) return;

        const parentItem = toggle.closest('li.has-submenu');
        const menu = getDirectSubmenu(parentItem);
        if (!menu) return;

        const isOpen = menu.classList.contains('show');

        if (isOpen) {
            closeBranch(parentItem, { updateAncestors: true });
            return;
        }

        closeSiblingBranches(parentItem);
        openBranch(parentItem);
    });

    // --- Wspólny resize handler ---
    window.addEventListener('resize', () => {
        if (window.innerWidth >= MOBILE_BREAKPOINT) {
            if (navbarCollapse && navbarCollapse.classList.contains('show')) {
                bsCollapse?.hide();
                body.classList.remove('mobile-menu-open');
            }

            resetAllSubmenus();
            desktopSubmenuApi?.recalculateOpenDirections();
            return;
        }

        document.querySelectorAll('.navbar-nav li.has-submenu').forEach((item) => {
            item.classList.remove('open-left', 'open-right');
        });

        document.querySelectorAll('.navbar-nav .dropdown-menu').forEach((menu) => {
            menu.classList.remove('align-right');
        });
    });

    // Zwracamy referencję dla initHeader
    return navbarCollapse;
}
